/**
 * The screen as data (docs/plans/os-live-mode.md, Decided 10).
 *
 * Every chat turn, and every live question, carries what the person has in
 * front of them: the cut of the list, the rows in view, what is selected, what
 * is open. Only the brain reads it; the voice is only the mouth. It is capped
 * on the device before it leaves and again on the server as it is read, so a
 * long list never becomes a long prompt: at most `AGENT_VIEW_ROWS_MAX` rows,
 * each with a few short values, and a shown total for the rest.
 *
 * `readAgentView` is the one reader. It is loose the way the rest of the
 * screen context is: a part it does not recognise is dropped, never the whole
 * view. Ids must look like ids, because the server checks each one against
 * the person's access before the model sees it, and a screen can never widen
 * what OS reads.
 */

import { dateConditionValueProblem, dateOpTakesDate, dateOpTakesDays, isDateConditionOp, type DateConditionOp } from './date-conditions';

/** One condition the list is cut by, in the screen's own words: `Payment mode` is `UPI, Cash`. */
export type AgentViewFilter = { readonly field: string; readonly is: string };

/** One field's value on a row, as the screen draws it. */
export type AgentViewCell = { readonly field: string; readonly value: string };

/** One row in view: an entry, or a record on a list of records. */
export type AgentViewRow = { readonly id: string; readonly values: readonly AgentViewCell[] };

/**
 * What a panel or a drawer over the page has open, or the one thing a page is
 * about: a link's own page (web-redesign-10 phase 3) has that link open, so
 * "this link" means it.
 */
export type AgentViewOpen = { readonly kind: 'entry' | 'record' | 'link'; readonly id: string };

export type AgentView = {
  /** The tab of the page that is showing, in the screen's words. */
  readonly tab?: string;
  /** The days the list is cut to, inclusive, as YYYY-MM-DD. */
  readonly range?: { readonly from: string; readonly to: string };
  readonly filters?: readonly AgentViewFilter[];
  readonly sort?: { readonly field: string; readonly dir: 'asc' | 'desc' };
  /** The words in the page's search box. */
  readonly search?: string;
  /** How many rows are drawn, how many the cut holds, and what they come to, as the screen says it. */
  readonly shown?: { readonly count: number; readonly total: number; readonly sum?: string };
  readonly rows?: readonly AgentViewRow[];
  /** The ids of the rows the person ticked. */
  readonly selected?: readonly string[];
  readonly open?: AgentViewOpen;
  /**
   * An entry being written and not saved yet: what its fields hold so far.
   * Present and empty when the new-entry page is open with nothing in it yet
   * (web-redesign-10 phase 7): OS may fill it (`fill_entry`) whenever it is there.
   */
  readonly draft?: readonly AgentViewCell[];
};

/** The most rows a view carries; `shown` says how many more there are. */
export const AGENT_VIEW_ROWS_MAX = 20;
/** The most values one row carries. */
export const AGENT_VIEW_CELLS_MAX = 8;
/**
 * The most values the draft carries: a whole form, not a row's few cells, so
 * OS reads every field the person has filled (web-redesign-10 phase 7). As
 * many as one fill may carry.
 */
export const AGENT_VIEW_DRAFT_MAX = 40;
/** The longest value, in characters, before it is cut with an ellipsis. */
export const AGENT_VIEW_VALUE_MAX = 60;
/** The longest field name, tab name or filter answer. */
export const AGENT_VIEW_LABEL_MAX = 80;
/** The most filter conditions a view carries. */
export const AGENT_VIEW_FILTERS_MAX = 12;
/** The most selected ids a view carries. */
export const AGENT_VIEW_SELECTED_MAX = 50;

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const isObject = (raw: unknown): raw is Record<string, unknown> => typeof raw === 'object' && raw !== null && !Array.isArray(raw);

/** One line of words, trimmed and cut to `max` with an ellipsis; undefined when nothing is left. */
const short = (raw: unknown, max: number): string | undefined => {
  if (typeof raw !== 'string' && typeof raw !== 'number') return undefined;
  const line = String(raw).replace(/\s+/g, ' ').trim();
  if (line === '') return undefined;
  return line.length <= max ? line : `${line.slice(0, max - 1).trimEnd()}…`;
};

/**
 * One value as a view carries it: one line, cut to `AGENT_VIEW_VALUE_MAX`, or
 * undefined when nothing is left. A page compares what it holds now with what
 * OS was shown through this, so the two are cut alike.
 */
export const agentViewWords = (raw: unknown): string | undefined => short(raw, AGENT_VIEW_VALUE_MAX);

const idOf = (raw: unknown): string | undefined => (typeof raw === 'string' && ID.test(raw) ? raw.toLowerCase() : undefined);

const count = (raw: unknown): number | undefined =>
  typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : undefined;

const cellsOf = (raw: unknown, max: number = AGENT_VIEW_CELLS_MAX): AgentViewCell[] => {
  if (!Array.isArray(raw)) return [];
  const cells: AgentViewCell[] = [];
  for (const one of raw) {
    if (cells.length >= max) break;
    if (!isObject(one)) continue;
    const field = short(one.field, AGENT_VIEW_LABEL_MAX);
    const value = short(one.value, AGENT_VIEW_VALUE_MAX);
    if (field !== undefined && value !== undefined) cells.push({ field, value });
  }
  return cells;
};

const rowsOf = (raw: unknown): AgentViewRow[] => {
  if (!Array.isArray(raw)) return [];
  const rows: AgentViewRow[] = [];
  const seen = new Set<string>();
  for (const one of raw) {
    if (rows.length >= AGENT_VIEW_ROWS_MAX) break;
    if (!isObject(one)) continue;
    const id = idOf(one.id);
    if (id === undefined || seen.has(id)) continue;
    seen.add(id);
    rows.push({ id, values: cellsOf(one.values) });
  }
  return rows;
};

const filtersOf = (raw: unknown): AgentViewFilter[] => {
  if (!Array.isArray(raw)) return [];
  const filters: AgentViewFilter[] = [];
  for (const one of raw) {
    if (filters.length >= AGENT_VIEW_FILTERS_MAX) break;
    if (!isObject(one)) continue;
    const field = short(one.field, AGENT_VIEW_LABEL_MAX);
    const is = short(one.is, AGENT_VIEW_LABEL_MAX);
    if (field !== undefined && is !== undefined) filters.push({ field, is });
  }
  return filters;
};

const idsOf = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  const ids = new Set<string>();
  for (const one of raw) {
    if (ids.size >= AGENT_VIEW_SELECTED_MAX) break;
    const id = idOf(one);
    if (id !== undefined) ids.add(id);
  }
  return [...ids];
};

/**
 * A view, read loosely and capped. Undefined when nothing in it survives, so
 * an empty view never reaches the prompt as an empty section.
 */
export const readAgentView = (raw: unknown): AgentView | undefined => {
  if (!isObject(raw)) return undefined;
  const tab = short(raw.tab, AGENT_VIEW_LABEL_MAX);
  const range =
    isObject(raw.range) && typeof raw.range.from === 'string' && typeof raw.range.to === 'string' && DAY.test(raw.range.from) && DAY.test(raw.range.to)
      ? { from: raw.range.from, to: raw.range.to }
      : undefined;
  const filters = filtersOf(raw.filters);
  const sortField = isObject(raw.sort) ? short(raw.sort.field, AGENT_VIEW_LABEL_MAX) : undefined;
  const sortDir = isObject(raw.sort) && (raw.sort.dir === 'asc' || raw.sort.dir === 'desc') ? raw.sort.dir : undefined;
  const search = short(raw.search, AGENT_VIEW_LABEL_MAX);
  const shownCount = isObject(raw.shown) ? count(raw.shown.count) : undefined;
  const shownTotal = isObject(raw.shown) ? count(raw.shown.total) : undefined;
  const sum = isObject(raw.shown) ? short(raw.shown.sum, AGENT_VIEW_VALUE_MAX) : undefined;
  const rows = rowsOf(raw.rows);
  const selected = idsOf(raw.selected);
  const openId = isObject(raw.open) ? idOf(raw.open.id) : undefined;
  const openKind = isObject(raw.open) && (raw.open.kind === 'entry' || raw.open.kind === 'record' || raw.open.kind === 'link') ? raw.open.kind : undefined;
  // An empty draft still says a new entry is open; no draft says none is.
  const draft = Array.isArray(raw.draft) ? cellsOf(raw.draft, AGENT_VIEW_DRAFT_MAX) : undefined;

  const view: AgentView = {
    ...(tab === undefined ? {} : { tab }),
    ...(range === undefined ? {} : { range }),
    ...(filters.length === 0 ? {} : { filters }),
    ...(sortField === undefined || sortDir === undefined ? {} : { sort: { field: sortField, dir: sortDir } }),
    ...(search === undefined ? {} : { search }),
    ...(shownCount === undefined || shownTotal === undefined
      ? {}
      : { shown: { count: shownCount, total: Math.max(shownTotal, shownCount), ...(sum === undefined ? {} : { sum }) } }),
    ...(rows.length === 0 ? {} : { rows }),
    ...(selected.length === 0 ? {} : { selected }),
    ...(openId === undefined || openKind === undefined ? {} : { open: { kind: openKind, id: openId } }),
    ...(draft === undefined ? {} : { draft }),
  };
  return Object.keys(view).length === 0 ? undefined : view;
};

/** Every id a view names: its rows, its selection and what it has open. The server checks each one. */
export const agentViewIds = (view: AgentView): string[] => [
  ...new Set([...(view.rows ?? []).map((row) => row.id), ...(view.selected ?? []), ...(view.open === undefined ? [] : [view.open.id])]),
];

/**
 * The view with only the ids `mayOpen` lets through: a row, a selected id or
 * an open panel the person may not open is dropped, and the rest stays. The
 * shown count is left as it is: it is the screen's own number.
 */
export const agentViewWithin = (view: AgentView, mayOpen: (id: string) => boolean): AgentView => {
  const rows = (view.rows ?? []).filter((row) => mayOpen(row.id));
  const selected = (view.selected ?? []).filter(mayOpen);
  const open = view.open !== undefined && mayOpen(view.open.id) ? view.open : undefined;
  const { rows: _rows, selected: _selected, open: _open, ...rest } = view;
  return {
    ...rest,
    ...(rows.length === 0 ? {} : { rows }),
    ...(selected.length === 0 ? {} : { selected }),
    ...(open === undefined ? {} : { open }),
  };
};

/* ------------------------------------------------------------ set_view */

/**
 * OS changes the view (docs/plans/os-live-mode.md, Decided 11 and 3.1).
 *
 * The `set_view` tool answers a patch for one record's list, and the app
 * applies it: the web's record page and the phone's record screen. Nothing is
 * written on the server. The patch speaks the filter's own language (field
 * ids, option and row ids, the Days pills' presets), already resolved and
 * checked on the server, so the app turns it into its filter with no guessing.
 *
 * What a patch leaves out stays as the person had it: `days` absent keeps the
 * days, `filters` absent keeps every field's condition. `filters` present
 * replaces them all (`[]` takes every one off), and `days: null` is all days.
 */
export const AGENT_VIEW_PRESETS = ['today', 'yesterday', 'week', 'month', 'last_month'] as const;
export type AgentViewPreset = (typeof AGENT_VIEW_PRESETS)[number];

/** The days a patch cuts the list to: a Days pill, or a range of days. */
export type AgentViewDays = { readonly preset: AgentViewPreset } | { readonly from: string; readonly to: string };

/** One field's condition, as the list's own filter holds it (`EntryFieldFilter` on the phone). */
export type AgentViewCondition =
  | { readonly fieldId: string; readonly op: 'any_of' | 'none_of'; readonly values: readonly (string | number)[] }
  | { readonly fieldId: string; readonly op: 'gte' | 'lte'; readonly value: number }
  | { readonly fieldId: string; readonly op: 'set' | 'unset' }
  /** A date condition (F21 of docs/plans/field-relations.md): the count of days or the date it takes, absent on the three about today. */
  | { readonly fieldId: string; readonly op: DateConditionOp; readonly value?: number | string };

export type AgentViewPatch = {
  /** The record whose list this is. */
  readonly recordId: string;
  /** What the view now shows, in a few words, for the note over the list ("UPI, this week"). */
  readonly what: string;
  /** Absent keeps the days; null is all days. */
  readonly days?: AgentViewDays | null;
  /** Absent keeps the conditions; present replaces them all. */
  readonly filters?: readonly AgentViewCondition[];
  /** Words to search the list for; null leaves the search. Absent keeps it. */
  readonly search?: string | null;
  /** An entry to open over the list; null closes the one open. Absent keeps it. */
  readonly open?: string | null;
};

/** What `set_view` answers: the patch, for the app to apply. */
export type ViewOutput = { readonly kind: 'view'; readonly patch: AgentViewPatch };

/** The longest `what` a patch carries. */
export const AGENT_VIEW_WHAT_MAX = 80;
/** The most values one condition carries. */
export const AGENT_VIEW_VALUES_MAX = 20;

const OPS_MANY = ['any_of', 'none_of'] as const;
const OPS_BOUND = ['gte', 'lte'] as const;
const OPS_PRESENCE = ['set', 'unset'] as const;

const fieldIdOf = (raw: unknown): string | undefined =>
  typeof raw === 'string' && raw.trim() !== '' && raw.length <= 120 ? raw : undefined;

const conditionOf = (raw: unknown): AgentViewCondition | undefined => {
  if (!isObject(raw)) return undefined;
  const fieldId = fieldIdOf(raw.fieldId);
  if (fieldId === undefined) return undefined;
  const op = raw.op;
  if ((OPS_MANY as readonly unknown[]).includes(op)) {
    if (!Array.isArray(raw.values)) return undefined;
    const values = raw.values
      .filter((one): one is string | number => (typeof one === 'string' && one !== '' && one.length <= 200) || (typeof one === 'number' && Number.isFinite(one)))
      .slice(0, AGENT_VIEW_VALUES_MAX);
    return values.length === 0 ? undefined : { fieldId, op: op as (typeof OPS_MANY)[number], values };
  }
  if ((OPS_BOUND as readonly unknown[]).includes(op)) {
    return typeof raw.value === 'number' && Number.isFinite(raw.value) ? { fieldId, op: op as (typeof OPS_BOUND)[number], value: raw.value } : undefined;
  }
  if ((OPS_PRESENCE as readonly unknown[]).includes(op)) return { fieldId, op: op as (typeof OPS_PRESENCE)[number] };
  if (isDateConditionOp(op)) {
    if (dateConditionValueProblem(op, raw.value) !== null) return undefined;
    if (dateOpTakesDays(op)) return { fieldId, op, value: Number(raw.value) };
    if (dateOpTakesDate(op)) return { fieldId, op, value: String(raw.value) };
    return { fieldId, op };
  }
  return undefined;
};

const daysOf = (raw: unknown): AgentViewDays | null | undefined => {
  if (raw === null) return null;
  if (!isObject(raw)) return undefined;
  if ((AGENT_VIEW_PRESETS as readonly unknown[]).includes(raw.preset)) return { preset: raw.preset as AgentViewPreset };
  if (typeof raw.from === 'string' && typeof raw.to === 'string' && DAY.test(raw.from) && DAY.test(raw.to)) {
    return raw.from <= raw.to ? { from: raw.from, to: raw.to } : { from: raw.to, to: raw.from };
  }
  return undefined;
};

/**
 * A `set_view` patch, read loosely off a tool part, or null when it is not
 * one. A part it does not recognise is dropped, never the whole patch; a patch
 * with no record, or with nothing to change, is null.
 */
export const readAgentViewPatch = (raw: unknown): AgentViewPatch | null => {
  if (!isObject(raw)) return null;
  const recordId = idOf(raw.recordId);
  const what = short(raw.what, AGENT_VIEW_WHAT_MAX);
  if (recordId === undefined || what === undefined) return null;
  const days = 'days' in raw ? daysOf(raw.days) : undefined;
  const filters = Array.isArray(raw.filters)
    ? raw.filters.slice(0, AGENT_VIEW_FILTERS_MAX).flatMap((one) => {
        const condition = conditionOf(one);
        return condition === undefined ? [] : [condition];
      })
    : undefined;
  const search = raw.search === null ? null : short(raw.search, AGENT_VIEW_LABEL_MAX);
  const open = raw.open === null ? null : idOf(raw.open);
  const patch: AgentViewPatch = {
    recordId,
    what,
    ...(days === undefined ? {} : { days }),
    ...(filters === undefined ? {} : { filters }),
    ...(search === undefined ? {} : { search }),
    ...(open === undefined ? {} : { open }),
  };
  return Object.keys(patch).length === 2 ? null : patch;
};

/** The patch inside a `set_view` output, read loosely, or null. */
export const viewPatchOf = (output: unknown): AgentViewPatch | null =>
  isObject(output) && output.kind === 'view' ? readAgentViewPatch(output.patch) : null;
